<script setup lang="ts">
import { gsap } from 'gsap'

const { t } = useI18n()

// Declares the `v-model` that `faqs.vue` binds. Nothing in this component
// reads it back — see the note there: the hero has no search field yet.
defineModel<string>({ default: '' })

const root = ref<HTMLElement | null>(null)

onMounted(() => {
  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

  tl
    .fromTo('.faq-hero-badge',
      { opacity: 0, y: -20, scale: 0.85 },
      { opacity: 1, y: 0, scale: 1, duration: 0.6 },
    )
    .fromTo('.faq-hero-title',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.75, ease: 'power4.out' },
      '-=0.3',
    )
    .fromTo('.faq-hero-subtitle',
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, duration: 0.65 },
      '-=0.4',
    )
    .fromTo('.faq-hero-search',
      { opacity: 0, y: 20, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.6 },
      '-=0.3',
    )
    .fromTo(['.faq-star-blue', '.faq-star-yellow'],
      { opacity: 0, scale: 0, rotation: -30 },
      { opacity: 1, scale: 1, rotation: 0, duration: 0.5, ease: 'back.out(2)', stagger: 0.1 },
      '-=0.4',
    )

  gsap.to('.faq-star-yellow', { rotation: '+=360', duration: 18, repeat: -1, ease: 'none' })
  gsap.to('.faq-star-blue', { rotation: '-=360', duration: 14, repeat: -1, ease: 'none' })
})
</script>

<template>
  <section id="faq-hero" ref="root" class="pt-36 pb-10 bg-white relative overflow-hidden flex items-center">

    <!-- Dot grid -->
    <div
      class="absolute inset-0 bg-[radial-gradient(circle,_#d1d5db_1.5px,_transparent_1.5px)] [background-size:28px_28px] opacity-50 pointer-events-none"
      aria-hidden="true" />

    <div class="container relative z-10 flex flex-col items-center text-center">
      <!-- Content wrapper — stars are anchored to this block -->
      <div class="relative w-full max-w-2xl flex flex-col items-center">

        <!-- Decorative sparkle stars -->
        <img
          src="/illustrations/star-blue.svg"
          alt=""
          aria-hidden="true"
          class="faq-star-blue absolute bottom-8 -start-6 md:-start-14 w-10 md:w-14 pointer-events-none">
        <img
          src="/illustrations/star-yellow.svg"
          alt=""
          aria-hidden="true"
          class="faq-star-yellow absolute top-0 -end-6 md:-end-14 w-12 md:w-16 pointer-events-none">

        <!-- Badge -->
        <div class="faq-hero-badge bfm-badge bg-creamy-yellow text-gray-900 px-4 py-2 text-sm md:text-base mb-7">
          <UIcon name="i-heroicons-chat-bubble-oval-left-ellipsis" class="text-heart-purple size-5" />
          {{ t('page.faq.hero.badge') }}
        </div>

        <!-- Title -->
        <h1 class="faq-hero-title text-[clamp(2rem,5vw,3.75rem)] font-bold leading-[1.1] rtl:leading-[1.35] mb-5">
          <span class="text-gray-900">{{ t('page.faq.hero.title_1') }}</span><span class="text-heart-purple">{{ t('page.faq.hero.title_2') }}</span>
        </h1>

        <!-- Subtitle -->
        <p class="faq-hero-subtitle text-sm sm:text-base lg:text-lg text-gray-500 leading-relaxed max-w-sm sm:max-w-md md:max-w-xl mx-auto mb-8 md:mb-10">
          {{ t('page.faq.hero.subtitle') }}
        </p>
      </div>
    </div>
  </section>
</template>
